import { FilterValues } from '@/app/[locale]/collection/types/filter-store-v2.types'
import {
  buildCollectionUrl as buildCollectionUrlUtil,
  buildQueryParams,
} from '@/app/[locale]/collection/utils/url-utils'
import { ProductFormatter } from '@/services/formatter/__f.product'
import { Collection2Response, TCollectionData } from '@/types/collection'
import type {
  ProductCollectionResponse,
  ProductQueryParams,
  ProductResponseFormatted,
  X2CoolCashResponse,
} from '@/types/product'
import { ApiResponse, httpClient } from './http-client'

// ===== OLD COLLECTION API (V3) =====
export const __cCollectionService = {
  async getProductsBySlug(
    params: ProductQueryParams
  ): Promise<ProductResponseFormatted> {
    try {
      const response = await httpClient.V3.get<ProductCollectionResponse>(
        `/collections/list-products/${params.slug}`,
        {
          params: {
            ...params,
            visibility: true,
          },
        }
      )

      // Format the response data directly in the service
      return ProductFormatter.reFormatProductsByCollectionResponse(response)
    } catch (error) {
      console.error('Failed to fetch products by slug:', error)
      throw new Error(`Failed to fetch products for collection ${params.slug}`)
    }
  },

  async getX2CoolCashProducts() {
    //TODO: set theo collection nhưng BE lại trả về toàn bộ product_id thuộc các collection
    try {
      const { data } =
        await httpClient.V3.get<ApiResponse<X2CoolCashResponse>>(
          '/get-sale-coolcash'
        )
      return data
    } catch (error) {
      console.error('Failed to fetch x2 coolcash products:', error)
      throw new Error('Failed to fetch x2 coolcash products')
    }
  },
}

// ===== NEW COLLECTION API (V4_NEW) =====
export class __cCollection2Service {
  /**
   * Lấy dữ liệu collection từ client-side với filter values
   * @param slug - Full collection slug (base slug + concat value nếu có)
   *               Ví dụ: 'ao-nu-xam' (base: 'ao-nu', concat: 'xam')
   * @param filters - Filter values từ store
   * @returns Collection data
   */
  static async getCollectionData(
    slug: string,
    filters: FilterValues
  ): Promise<TCollectionData> {
    try {
      // Build query params từ filters
      const queryParams = this.buildQueryParams(filters)

      const response = await httpClient.V4_NEW.get<Collection2Response>(
        `/products/by-collection-slug/${slug}`,
        {
          params: {
            ...queryParams,
            skip_facets: true,
            skip_banner: true,
          },
        }
      )

      if (!response.data) {
        throw new Error('No data returned from API')
      }

      // Re-format the product list to ensure data consistency
      return {
        ...response.data,
        products: ProductFormatter.reFormatProductList(response.data.products),
      }
    } catch (error) {
      console.error(
        `[CLIENT_SERVICE] Failed to fetch collection data for slug: ${slug}`,
        error
      )
      throw new Error(
        `Failed to fetch collection data: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Build query parameters từ filter values
   * @param filters - Filter values từ store
   * @returns Query parameters object
   */
  private static buildQueryParams(
    filters: FilterValues
  ): Record<string, unknown> {
    return buildQueryParams(filters)
  }

  /**
   * Build URL immediately khi user click filter (optimistic)
   * @param currentUrl - Current URL
   * @param filterKey - Filter key being changed
   * @param filterValue - New filter value
   * @param basePath - Base path
   * @returns New URL immediately
   */
  static buildUrlImmediately(
    currentUrl: string,
    filterKey: string,
    filterValue: string | string[] | undefined
  ): string {
    const url = new URL(currentUrl)

    // Preserve non-filter params before processing filter changes
    // Extract main_product_id from URL if present (for gift shop functionality)
    // This is a general preserve mechanism, specific collections can use getPreservedParamsForCollection
    const preservedParams: string[] = ['main_product_id'] // Can be extended later
    const preservedValues: Record<string, string> = {}
    preservedParams.forEach((param) => {
      const value = url.searchParams.get(param)
      if (value) {
        preservedValues[param] = value
      }
    })

    // Xóa tất cả params cũ của filterKey trước khi set mới
    url.searchParams.delete(filterKey)

    // Handle filter removal
    if (
      filterValue === undefined ||
      filterValue === null ||
      filterValue === ''
    ) {
      // Đã xóa ở trên, không cần làm gì thêm
    } else if (Array.isArray(filterValue)) {
      if (filterValue.length > 0) {
        url.searchParams.set(filterKey, filterValue.join(','))
      }
    } else {
      url.searchParams.set(filterKey, String(filterValue))
    }

    // Restore preserved params after setting filter params
    Object.entries(preservedValues).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })

    // Remove page=1
    if (url.searchParams.get('page') === '1') {
      url.searchParams.delete('page')
    }

    return url.pathname + (url.search ? url.search : '')
  }

  /**
   * Build collection URL với filter concatenation (simplified version)
   * @param originalSlug - Original collection slug
   * @param filters - Filter values
   * @param basePath - Base path (/collection)
   * @param concatValue - Concat value từ store
   * @returns Complete collection URL
   */
  static buildCollectionUrl(
    originalSlug: string,
    filters: FilterValues,
    basePath: string = '/collection',
    concatValue?: string | null
  ): string {
    return buildCollectionUrlUtil(
      originalSlug,
      filters,
      basePath,
      undefined, // facets - không cần trong store v2
      undefined, // card_facets - không cần trong store v2
      undefined, // slugAnalysis - không cần trong store v2
      undefined, // currentPathname - không cần
      concatValue
    )
  }

  /**
   * Lấy body HTML của collection theo path (slug)
   * @param slug - Collection slug (có thể chứa concat values)
   * @returns Body HTML string
   * @throws Error nếu fetch fail
   */
  static async getBodyHtmlByCollectionPath(slug: string): Promise<string> {
    interface BodyHtmlResponse {
      state: boolean
      status_code: number
      message: string
      data: {
        body_html: string
      }
    }

    const response = await httpClient.V4_NEW.get<BodyHtmlResponse>(
      `/collections/get-body-html-by-collection-path/${slug}`
    )

    if (!response.data?.body_html) {
      throw new Error(`No body_html returned for slug: ${slug}`)
    }

    return response.data.body_html
  }
}
